import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock, MapPin, Plane, Shield, Mountain } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface QuoteResult {
  distance: number;
  time: string;
  subtotal: number;
  gratuity: number;
  total: number;
  gratuityIncluded?: boolean;
  isFixedRoute?: boolean;
  isFlat?: boolean;
  note?: string;
  breakdown: {
    baseFee: number;
    mileageCharge: number;
    lateNightFee: number;
    minimumApplied?: boolean;
    satMinimumFare?: number;
  };
}

// JBSA Military Installations (Gratuity INCLUDED) - Updated January 2026
const MILITARY_PRESETS = [
  { city: "Lackland AFB", price: 168, miles: 18 },
  { city: "Fort Sam Houston/BAMC", price: 132, miles: 7 },
  { city: "Randolph AFB", price: 180, miles: 20 },
  { city: "Camp Bullis", price: 167, miles: 19 },
];

// Hill Country flat-rate presets (Gratuity INCLUDED) - Updated January 2026
const HILL_COUNTRY_PRESETS = [
  { city: "Fair Oaks Ranch", price: 195, miles: 24 },
  { city: "Boerne", price: 225, miles: 32 },
  { city: "New Braunfels", price: 245, miles: 35 },
  { city: "Seguin", price: 250, miles: 36 },
  { city: "Canyon Lake", price: 285, miles: 45 },
  { city: "Lakehills", price: 265, miles: 38 },
  { city: "Wimberley", price: 295, miles: 45 },
];

const QuickQuoteCalculator = () => {
  const [pickupAddress, setPickupAddress] = useState("");
  const [dropoffAddress, setDropoffAddress] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [quoteResult, setQuoteResult] = useState<QuoteResult | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [presetType, setPresetType] = useState<'military' | 'hillcountry' | null>(null);

  const handleMilitaryPresetClick = async (preset: typeof MILITARY_PRESETS[0]) => {
    const nextPickup = "SAT Airport";
    const nextDropoff = `${preset.city}, TX`;

    setPickupAddress(nextPickup);
    setDropoffAddress(nextDropoff);
    setSelectedPreset(preset.city);
    setPresetType("military");

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("calculate-quote", {
        body: {
          pickupAddress: nextPickup,
          dropoffAddress: nextDropoff,
          pickupTime: pickupTime || "12:00",
        },
      });
      if (error) throw error;
      setQuoteResult(data);
      toast.success(`${preset.city}: $${preset.price} (gratuity included)`);
    } catch (e) {
      console.error("Error calculating preset quote:", e);
      // Fallback to the preset values if Google/edge function fails
      setQuoteResult({
        distance: preset.miles,
        time: `${Math.round(preset.miles * 1.2)} minutes`,
        subtotal: preset.price,
        gratuity: 0,
        total: preset.price,
        gratuityIncluded: true,
        isFlat: true,
        breakdown: {
          baseFee: preset.price,
          mileageCharge: 0,
          lateNightFee: 0,
        },
      });
      toast.error("Using estimated distance—please try again for live distance.");
    } finally {
      setLoading(false);
    }
  };

  const handleHillCountryPresetClick = async (preset: typeof HILL_COUNTRY_PRESETS[0]) => {
    const nextPickup = "SAT Airport";
    const nextDropoff = `${preset.city}, TX`;

    setPickupAddress(nextPickup);
    setDropoffAddress(nextDropoff);
    setSelectedPreset(preset.city);
    setPresetType("hillcountry");

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("calculate-quote", {
        body: {
          pickupAddress: nextPickup,
          dropoffAddress: nextDropoff,
          pickupTime: pickupTime || "12:00",
        },
      });
      if (error) throw error;
      setQuoteResult(data);
      toast.success(`${preset.city}: $${preset.price} (gratuity included)`);
    } catch (e) {
      console.error("Error calculating preset quote:", e);
      setQuoteResult({
        distance: preset.miles,
        time: `${Math.round(preset.miles * 1.2)} minutes`,
        subtotal: preset.price,
        gratuity: 0,
        total: preset.price,
        gratuityIncluded: true,
        isFlat: true,
        breakdown: {
          baseFee: preset.price,
          mileageCharge: 0,
          lateNightFee: 0,
        },
      });
      toast.error("Using estimated distance—please try again for live distance.");
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    if (!pickupAddress || !dropoffAddress || !pickupDate || !pickupTime) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    setSelectedPreset(null);
    setPresetType(null);

    try {
      // Check for Austin Airport flat rate override (gratuity NOT included)
      const isAustinAirport = dropoffAddress.toLowerCase().includes("austin") &&
                             (dropoffAddress.toLowerCase().includes("airport") ||
                              dropoffAddress.toLowerCase().includes("bergstrom") ||
                              dropoffAddress.toLowerCase().includes("aus"));

      if (isAustinAirport) {
        setQuoteResult({
          distance: 80,
          time: "1 hour 15 minutes",
          subtotal: 395,
          gratuity: 79,
          total: 474,
          gratuityIncluded: false,
          isFixedRoute: true,
          isFlat: true,
          breakdown: {
            baseFee: 395,
            mileageCharge: 0,
            lateNightFee: 0
          },
          note: "Austin airport flat rate. Gratuity (20%) calculated separately."
        });
        setLoading(false);
        return;
      }

      // Call edge function to calculate distance
      const { data, error } = await supabase.functions.invoke('calculate-quote', {
        body: {
          pickupAddress,
          dropoffAddress,
          pickupTime
        }
      });

      if (error) throw error;

      setQuoteResult(data);

    } catch (error) {
      console.error("Error calculating quote:", error);
      toast.error("Unable to calculate quote. Please check the addresses and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto bg-gradient-to-br from-black via-gray-900 to-black border-2 border-[#D4AF37]/30 shadow-2xl">
      <CardHeader className="space-y-2 text-center">
        <CardTitle className="text-3xl font-bold bg-gradient-to-r from-[#D4AF37] via-[#FFD700] to-[#D4AF37] bg-clip-text text-transparent">
          Quick Quote Calculator
        </CardTitle>
        <CardDescription className="text-gray-300 text-base">
          Get an instant estimate for your luxury car service
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* JBSA Military Installations */}
        <div className="space-y-3">
          <Label className="text-white flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-400" />
            JBSA Military Installations
          </Label>
          <p className="text-xs text-blue-400 -mt-1">
            Gratuity included in military rates ✓
          </p>
          <div className="grid grid-cols-2 gap-2 p-3 rounded-lg border border-blue-500/30 bg-blue-950/20">
            {MILITARY_PRESETS.map((preset) => (
              <Button
                key={preset.city}
                type="button"
                variant="outline"
                onClick={() => handleMilitaryPresetClick(preset)}
                className={`flex flex-col items-center py-3 h-auto transition-all duration-200 ${
                  selectedPreset === preset.city && presetType === 'military'
                    ? "bg-blue-500/20 border-blue-400 text-blue-300"
                    : "bg-gray-800/50 border-blue-500/30 text-gray-300 hover:border-blue-400/50 hover:bg-blue-950/30"
                }`}
              >
                <span className="font-semibold text-sm">{preset.city}</span>
                <span className="text-lg font-bold text-blue-400">${preset.price}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Hill Country Destinations */}
        <div className="space-y-3">
          <Label className="text-white flex items-center gap-2">
            <Mountain className="w-4 h-4 text-emerald-400" />
            Hill Country Destinations
          </Label>
          <p className="text-xs text-emerald-400 -mt-1">
            Gratuity included ✓ - no additional charges
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 rounded-lg border border-emerald-500/30 bg-emerald-950/20">
            {HILL_COUNTRY_PRESETS.map((preset) => (
              <Button
                key={preset.city}
                type="button"
                variant="outline"
                onClick={() => handleHillCountryPresetClick(preset)}
                className={`flex flex-col items-center py-3 h-auto transition-all duration-200 ${
                  selectedPreset === preset.city && presetType === 'hillcountry'
                    ? "bg-emerald-500/20 border-emerald-400 text-emerald-300"
                    : "bg-gray-800/50 border-emerald-500/30 text-gray-300 hover:border-emerald-400/50 hover:bg-emerald-950/30"
                }`}
              >
                <span className="font-semibold text-sm">{preset.city}</span>
                <span className="text-lg font-bold text-emerald-400">${preset.price}</span>
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex-1 border-t border-gray-700" />
          <span className="text-gray-500 text-sm">or enter custom route</span>
          <div className="flex-1 border-t border-gray-700" />
        </div>

        <div className="space-y-4">
          {/* Pickup Address */}
          <div className="space-y-2">
            <Label htmlFor="pickup" className="text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#D4AF37]" />
              Pickup Address
            </Label>
            <Input
              id="pickup"
              placeholder="Enter pickup location"
              value={pickupAddress}
              onChange={(e) => {
                setPickupAddress(e.target.value);
                setSelectedPreset(null);
                setPresetType(null);
              }}
              className="bg-gray-800 border-[#D4AF37]/30 text-white placeholder:text-gray-400 focus:border-[#D4AF37]"
            />
          </div>

          {/* Drop-off Address */}
          <div className="space-y-2">
            <Label htmlFor="dropoff" className="text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#D4AF37]" />
              Drop-off Address
            </Label>
            <Input
              id="dropoff"
              placeholder="Enter destination"
              value={dropoffAddress}
              onChange={(e) => {
                setDropoffAddress(e.target.value);
                setSelectedPreset(null);
                setPresetType(null);
              }}
              className="bg-gray-800 border-[#D4AF37]/30 text-white placeholder:text-gray-400 focus:border-[#D4AF37]"
            />
            <p className="text-xs text-gray-400">
              SAT Airport pickups/drop-offs have an $85 minimum fare
            </p>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date" className="text-white flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#D4AF37]" />
                Pickup Date
              </Label>
              <Input
                id="date"
                type="date"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                className="bg-gray-800 border-[#D4AF37]/30 text-white focus:border-[#D4AF37]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="time" className="text-white flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#D4AF37]" />
                Pickup Time
              </Label>
              <Input
                id="time"
                type="time"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                className="bg-gray-800 border-[#D4AF37]/30 text-white focus:border-[#D4AF37]"
              />
              <p className="text-xs text-gray-400">
                Late night pickups (10 PM - 5 AM) include a $25 fee
              </p>
            </div>
          </div>
        </div>

        {/* Get Quote Button */}
        <Button
          onClick={calculatePrice}
          disabled={loading}
          className="w-full bg-gradient-to-r from-[#D4AF37] via-[#FFD700] to-[#D4AF37] text-black font-bold text-lg py-6 hover:shadow-2xl hover:shadow-[#D4AF37]/50 transition-all duration-300"
        >
          {loading ? "Calculating..." : "Get Quote"}
        </Button>

        {/* Quote Result */}
        {quoteResult && (
          <div className="mt-6 p-6 bg-gray-800/50 rounded-lg border-2 border-[#D4AF37]/30 space-y-4 animate-fade-in">
            {/* Header Badge */}
            {selectedPreset && (
              <div className="text-center pb-3 border-b border-[#D4AF37]/30">
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  presetType === 'military' 
                    ? "bg-blue-500/20 text-blue-300"
                    : "bg-emerald-500/20 text-emerald-300"
                }`}>
                  {presetType === 'military' 
                    ? `✓ Military Rate — Gratuity Included`
                    : `✓ Hill Country Rate — Gratuity Included`
                  }
                </span>
              </div>
            )}

            {/* Investment Summary Header */}
            <div className="text-center">
              <h3 className="text-lg font-semibold text-[#D4AF37]">Investment Summary</h3>
              {selectedPreset && (
                <p className="text-sm text-gray-400 mt-1">
                  {presetType === 'military' ? 'Military Installation' : 'Hill Country'} Airport Transfer (SAT)
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 text-white">
              <div>
                <p className="text-sm text-gray-400">Estimated Distance</p>
                <p className="text-lg font-semibold">{quoteResult.distance} miles</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Estimated Time</p>
                <p className="text-lg font-semibold">{quoteResult.time}</p>
              </div>
            </div>

            <div className="border-t border-[#D4AF37]/30 pt-4 space-y-2">
              {quoteResult.gratuityIncluded ? (
                // Gratuity INCLUDED display
                <>
                  <div className="flex justify-between text-white">
                    <span>Flat Rate Service:</span>
                    <span>${quoteResult.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-white">
                    <span>Subtotal:</span>
                    <span>${quoteResult.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-400">
                    <span>Gratuity (20%):</span>
                    <span>✓ Included</span>
                  </div>
                </>
              ) : quoteResult.isFlat ? (
                // Flat rate without gratuity included (Austin/Houston)
                <>
                  <div className="flex justify-between text-white">
                    <span>Base Service:</span>
                    <span>${quoteResult.subtotal.toFixed(2)}</span>
                  </div>
                  {quoteResult.gratuity > 0 && (
                    <div className="flex justify-between text-white">
                      <span>Gratuity (20%):</span>
                      <span>${quoteResult.gratuity.toFixed(2)}</span>
                    </div>
                  )}
                </>
              ) : (
                // Formula-based pricing
                <>
                  <div className="flex justify-between text-white">
                    <span>Subtotal:</span>
                    <span>${quoteResult.subtotal.toFixed(2)}</span>
                  </div>
                  {quoteResult.breakdown.minimumApplied && (
                    <div className="flex justify-between text-emerald-400 text-sm">
                      <span>✓ SAT Airport Minimum Applied:</span>
                      <span>${quoteResult.breakdown.satMinimumFare?.toFixed(2)}</span>
                    </div>
                  )}
                  {quoteResult.breakdown.lateNightFee > 0 && (
                    <div className="flex justify-between text-amber-400">
                      <span>Late Night Fee (10 PM - 5 AM):</span>
                      <span>+${quoteResult.breakdown.lateNightFee.toFixed(2)}</span>
                    </div>
                  )}
                  {quoteResult.gratuity > 0 && (
                    <div className="flex justify-between text-white">
                      <span>Gratuity (20%):</span>
                      <span>${quoteResult.gratuity.toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between text-2xl font-bold text-[#D4AF37] pt-4 border-t border-[#D4AF37]/30">
                <span>Total Investment:</span>
                <span>${quoteResult.total.toFixed(2)}</span>
              </div>
            </div>

            <p className="text-xs text-gray-400 text-center pt-2">
              {quoteResult.gratuityIncluded
                ? `*Flat rate for SAT Airport ↔ ${selectedPreset}. Gratuity included - no additional charges.`
                : quoteResult.isFlat
                  ? "*Flat rate. Gratuity calculated separately."
                  : quoteResult.note || "*Final price may vary based on actual route and traffic conditions"}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuickQuoteCalculator;